import type {HeadersArgs, LoaderFunctionArgs, MetaFunction} from 'react-router'
import {useLoaderData} from 'react-router'

import {json} from '@core/lib/json'
import {getSession} from '@core/auth/session.server'
import {getProductId} from '@core/products/registry'
import {readTilesByPackSlug} from '@core/wall/tiles.server'
import {mergeMeta} from '@core/lib/mergeMeta'
import {buildBreadcrumbList, buildFAQPage, buildImageObject, jsonLd} from '@core/lib/jsonld'
import {packOgImageUrl} from '@core/og/cardUrls'
import {TOILE_PACK_SAMPLES_BASE_URL} from '@product/toile/config'
import {getPackBySlug} from '@product/toile/packs/registry'
import {packCells} from '@product/toile/packs/data'
import {getCatalogCards} from '@product/toile/packs/catalog'
import {DEFAULT_RELATED_TITLE, resolveRelatedPacks} from '@product/toile/packs/relatedPacks'
import {PackLandingTemplate, packFaq} from '@product/toile/packs/PackLandingTemplate'

const TOILE_BASE_URL = 'https://toile.pictures'

export async function loader({params, request}: LoaderFunctionArgs) {
    const pack = getPackBySlug(params.packSlug ?? '')
    if (!pack) throw new Response('Not Found', {status: 404})

    // The page verb needs to know whether it can write (wall plan e; BRIEF §10 — nothing is written
    // anonymously). Same reading as the wall's own loader.
    //
    // And the nine cells as the WALL knows them: a cell tapped on the page is a quick look that
    // lands armed on the wall (BRIEF §9), which needs the tile's id. `readTilesByPackSlug` is the
    // reader plan c already added for `enqueuePack` — the plan's own § Files names a new
    // `readTilesForPack`, which would be a second query against the same index for the same rows.
    // Only the ids leave this function: the row carries `prompt_text`, and the prompt never renders
    // (`general_generator` head — the prompt is the asset, it lives in Postgres).
    const [session, tiles] = await Promise.all([
        getSession(request.headers.get('cookie')),
        readTilesByPackSlug(getProductId(), pack.slug),
    ])
    const signedIn = !!session.get('userId')
    const tileIdsByPosition = packCells(pack).map((_, position) => (
        tiles.find((t) => t.position === position)?.id ?? null
    ))

    // Related strip (pack page canon convergence, 2026-09-01) — resolved here, not in the
    // template, mirroring narcis's/ovid's own loader split: the template only ever receives
    // serializable card data. `catalog.ts`'s cards are already the wire-ready `PackCardView` shape,
    // so no extra join is needed the way narcis/ovid's registry+catalog join requires.
    const cards = getCatalogCards()
    const relatedCards = resolveRelatedPacks(pack.slug, cards)
    const relatedPacksCards = relatedCards.map((c) => ({
        slug: c.slug,
        title: c.name,
        cover: c.cover_portrait,
        meta: `9 samples · ${c.cat}`,
    }))

    return json(
        {pack, relatedTitle: DEFAULT_RELATED_TITLE, relatedPacksCards, signedIn, tileIdsByPosition},
        // The anonymous document is unchanged — a public page, stating nothing about caching, as it
        // has since it shipped. The signed-in one carries `signedIn: true` and takes the rule every
        // signed-in wall surface follows (`_layout._index.tsx`'s own): a shared cache must never
        // hand one visitor's state to the next. Toile's CloudFront default behaviour is
        // CachingDisabled today, so this states the rule rather than changing behaviour.
        signedIn ? {headers: {'Cache-Control': 'private, no-store'}} : undefined,
    )
}

/**
 * React Router 7 propagates only `Set-Cookie` from a loader's own response onto the DOCUMENT
 * response — every other header needs this export (`_layout._index.tsx` carries the same note and
 * the same three lines).
 */
export function headers({loaderHeaders}: HeadersArgs) {
    const cacheControl = loaderHeaders.get('Cache-Control')
    return cacheControl ? {'Cache-Control': cacheControl} : {}
}

export const meta: MetaFunction<typeof loader> = ({data, matches}) => {
    if (!data) return mergeMeta(matches, [])
    const {pack} = data
    // `pack.seo` is optional (Track A, `webapp/app/products/toile/packs/data.ts`) — a pack with none
    // renders today's byte-identical title/description. Populated by `pack promote --product toile
    // --seo-copy` (Track B, `thinking/plans/2026_08_31_toile_pack_wiring.md`).
    const title = pack.seo?.title ?? `${pack.name} · toile`
    const description = pack.seo?.metaDescription ?? pack.tagline
    const url = `${TOILE_BASE_URL}/${pack.slug}`
    // Same og:image/twitter block narcis/ovid's `packs/seo.ts` emit (og:image, secure_url, width
    // 1200, height 630, type image/png): the pack's card, an asset written when the pack ships.
    const ogImageUrl = packOgImageUrl('toile', TOILE_PACK_SAMPLES_BASE_URL, pack.slug)
    return mergeMeta(matches, [
        {title},
        {name: 'description', content: description},
        {property: 'og:title', content: title},
        {property: 'og:description', content: description},
        {property: 'og:url', content: url},
        {property: 'og:image', content: ogImageUrl},
        {property: 'og:image:secure_url', content: ogImageUrl},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {property: 'og:image:type', content: 'image/png'},
        {property: 'twitter:card', content: 'summary_large_image'},
        {property: 'twitter:url', content: url},
        {property: 'twitter:title', content: title},
        {property: 'twitter:description', content: description},
        {property: 'twitter:image', content: ogImageUrl},
        {tagName: 'link', rel: 'canonical', href: url},
    ])
}

/**
 * Toile pack page — the route is the document: loader, meta, the three JSON-LD schemas. The page
 * itself is `packs/PackLandingTemplate`, built on `core/packs/PackPageShell` (pack page canon
 * convergence, 2026-09-01, then the shell extraction, plan 2026_09_01_pack_page_shell.md step 3).
 * The FAQ comes from `packFaq` so the schema below and the rendered block are one array, not two
 * copies. The three schemas are still built here (the route owns the data the schemas need —
 * `packCells`, the samples base URL) and handed to the template as `jsonLd`, which forwards them
 * to the shell — same builders, same order, same position in the document stream as before (the
 * plan's §6 Q3: unify on "the template renders it," toile's route keeps building, the template/
 * shell render the `<script>` tags).
 */
export default function PackPage() {
    const {pack, relatedTitle, relatedPacksCards, signedIn, tileIdsByPosition} = useLoaderData<typeof loader>()
    const cells = packCells(pack)
    const base = `${TOILE_PACK_SAMPLES_BASE_URL}/${pack.slug}`

    const breadcrumbSchema = buildBreadcrumbList([
        {name: 'toile', url: 'https://toile.pictures/'},
        {name: 'All packs', url: 'https://toile.pictures/packs'},
        {name: pack.name, url: `https://toile.pictures/${pack.slug}`},
    ])

    const faqSchema = buildFAQPage(packFaq(pack))

    const imageGallerySchema = jsonLd('ImageGallery', {
        name: `${pack.name} — sample gallery`,
        description: pack.tagline,
        url: `https://toile.pictures/${pack.slug}`,
        image: cells.map((c, i) =>
            buildImageObject({
                contentUrl: `${base}/${c.image}`,
                name: `${pack.name} — ${c.subject}, ${c.style}`,
                caption: `${c.subject}, ${c.style}. ${c.palette}, ${c.framing}.`,
                ...(i === 0 && {representativeOfPage: true}),
            }),
        ),
    })

    return (
        <PackLandingTemplate
            pack={pack}
            relatedTitle={relatedTitle}
            relatedPacksCards={relatedPacksCards}
            jsonLd={[breadcrumbSchema, faqSchema, imageGallerySchema]}
            signedIn={signedIn}
            tileIdsByPosition={tileIdsByPosition}
        />
    )
}
